import { useState, useEffect, useCallback, useRef } from 'react';

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  /** True while ANY request is in flight (including deps-change refetches, where `loading`
   *  stays false after the first fetch). Callers that must never show data from a previous
   *  parameter set gate their render on `!fetching` — `data` still holds the prior response
   *  until the new one lands. */
  fetching: boolean;
  error: string | null;
  refresh: () => void;
  lastRefreshed: Date | null;
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: any[] = [],
  options?: { refreshInterval?: number; enabled?: boolean }
): UseApiResult<T> {
  // SPEC-142 §3(7): `enabled: false` defers the fetch until the caller flips it on
  // (e.g. its tab becomes active), so a multi-tab page stops firing every tab's
  // requests on mount. Default true — every existing call site is unchanged.
  const enabled = options?.enabled !== false;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const hasFetchedOnce = useRef(false);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;  // gated off — don't fetch yet (loading stays until enabled)
    let cancelled = false;
    // Only show loading skeleton on first fetch, not on auto-refreshes
    if (!hasFetchedOnce.current) {
      setLoading(true);
    }
    setFetching(true);
    setError(null);
    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
          setFetching(false);
          setLastRefreshed(new Date());
          hasFetchedOnce.current = true;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to fetch');
          setLoading(false);
          setFetching(false);
        }
      });
    return () => { cancelled = true; };
  }, [tick, enabled, ...deps]);

  // Auto-refresh interval (paused while gated off)
  useEffect(() => {
    if (!options?.refreshInterval || !enabled) return;
    const interval = setInterval(refresh, options.refreshInterval);
    return () => clearInterval(interval);
  }, [options?.refreshInterval, enabled, refresh]);

  return { data, loading, fetching, error, refresh, lastRefreshed };
}
